import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../img/logo.png';
import ref1 from '../img/referencia1.png';
import ref2 from '../img/referencia2.png';
import ref3 from '../img/referencia3.png';
import ref4 from '../img/referencia4.png';
import ref5 from '../img/referencia5.png';
import faqPreviewIllustration from '../img/404.png';
import { FAQS } from './faqs';
import { AJANLAT_PAGE_URL, CAMPAIGNS, isExternalCampaignHref } from './campaigns';
import { useI18n } from '../i18n';
import GdprConsentCheckbox from '../components/GdprConsentCheckbox';
import GoogleAddressAutocomplete from '../components/GoogleAddressAutocomplete';
import { SHOW_AKTUALIS_PROMOS } from '../siteFlags';
import usePricingSettings from '../hooks/usePricingSettings';
import { computeQuickCalculatorEstimate } from '../utils/csomagStyleEstimate';
import '../App.css';

function formatLandingQuickHuf(n) {
  return `${Math.round(Number(n)).toLocaleString('hu-HU')} Ft`;
}

async function fetchLandingDrivingKm(originPlaceId, destinationPlaceId, originAddress, destinationAddress) {
  const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8003').replace(/\/$/, '');
  const response = await fetch(`${apiBase}/api/distance/driving-km`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      originPlaceId: originPlaceId || undefined,
      destinationPlaceId: destinationPlaceId || undefined,
      originAddress,
      destinationAddress,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || 'Nem sikerült a távolság számítása.');
  }
  const distanceKm = Number(data?.distanceKm);
  if (!Number.isFinite(distanceKm) || distanceKm <= 0) {
    throw new Error('Érvénytelen távolság érkezett.');
  }
  return Math.round(distanceKm);
}

const PARTNER_LOGOS = [
  { src: ref1, alt: 'Partner logó 1' },
  { src: ref2, alt: 'Partner logó 2' },
  { src: ref3, alt: 'Partner logó 3' },
  { src: ref4, alt: 'Partner logó 4' },
  { src: ref5, alt: 'Partner logó 5' },
];

const GOOGLE_REVIEW_URL = 'https://g.page/r/CfQC1C2vwkP8EAE/review';

function optionalImg(fileName) {
  try {
    const asset = require(`../img/${fileName}`);
    return asset?.default || asset;
  } catch {
    return '';
  }
}

const SOCIAL_LINKS = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/moveandparcel',
    icon: optionalImg('facebook.png'),
    fallback: 'f',
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/moveandparcel.eu/',
    icon: optionalImg('instagram.png'),
    fallback: 'ig',
  },
];

const REVIEWS = [
  {
    initial: 'K',
    name: 'Kovács Dániel',
    rating: 5,
    avatarColor: '#1a73e8',
    meta: 'Google értékelés • 5,0 ★ • 2024',
    text:
      '„Pontosan érkeztek, minden bútor sérülésmentesen megérkezett. Rugalmasak, segítőkészek, biztosan őket választjuk legközelebb is.”',
  },
  {
    initial: 'M',
    name: 'Mészáros Anna',
    rating: 5,
    avatarColor: '#34a853',
    meta: 'Google értékelés • 5,0 ★ • 2023',
    text:
      '„Antik bútorok szállításához kerestünk partnert. Mindent fóliáztak, rögzítettek, végig profi kommunikáció. Bátran ajánlom másoknak is.”',
  },
  {
    initial: 'L',
    name: 'Lakatos Csaba',
    rating: 5,
    avatarColor: '#ea4335',
    meta: 'Google értékelés • 4,9 ★ • 2022',
    text:
      '„Rövid határidővel is vállalták a nemzetközi fuvart, minden papírt elintéztek. Korrekt ár, átlátható tájékoztatás.”',
  },
  {
    initial: 'S',
    name: 'Szabó Péter',
    rating: 5,
    avatarColor: '#f7931d',
    meta: 'Google értékelés • 5,0 ★ • 2022',
    text:
      '„Gyorsan reagáltak az ajánlatkérésre, pontos érkezés, barátságos sofőr. A motorkerékpár épségben megérkezett, profi rögzítéssel.”',
  },
  {
    initial: 'E',
    name: 'Erdei Zsófia',
    rating: 5,
    avatarColor: '#0f9d58',
    meta: 'Google értékelés • 5,0 ★ • 2021',
    text:
      '„Orvosi berendezések szállításánál kiemelten fontos a pontosság és a megbízhatóság. Minden a megbeszéltek szerint zajlott, ajánlom őket.”',
  },
];

function StarRow({ rating = 5 }) {
  const full = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <div className="review-stars" aria-label={`${full} / 5 értékelés`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          className={`review-star${i < full ? ' review-star--on' : ''}`}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M12 17.3l-6.2 3.6 1.7-7.1-5.5-4.8 7.2-.6L12 2l2.8 9.4 7.2.6-5.5 4.8 1.7 7.1L12 17.3z" />
        </svg>
      ))}
      <span className="review-google-badge" aria-hidden="true">
        G
      </span>
    </div>
  );
}

function ReviewMeta({ meta }) {
  const parts = String(meta || '')
    .split('•')
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div className="review-meta">
      {parts.map((p, idx) => {
        const isRating = p.includes('★');
        return (
          <React.Fragment key={`${idx}-${p}`}>
            {idx > 0 && <span className="review-meta-sep"> • </span>}
            <span className={isRating ? 'review-meta-rating' : undefined}>{p}</span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const NEWS_POSTS = [
  {
    isoDate: '2025-03-03',
    date: '2025. március 3.',
    title: 'Új EU‑útvonalak: gyorsabb árajánlat és rugalmas felvételi ablakok',
    excerpt:
      'Bővítettük a nemzetközi fuvarokhoz elérhető indulási napokat. Céges és magánmegbízásoknál egyaránt segítünk az időzítésben – kérj személyre szabott ajánlatot.',
    tag: 'Nemzetközi fuvar',
  },
  {
    isoDate: '2025-02-12',
    date: '2025. február 12.',
    title: 'Antik bútorok szállítása: 5 tipp a biztonságos csomagoláshoz',
    excerpt:
      'Összegyűjtöttük a leggyakoribb hibákat és a legjobb gyakorlatokat: védelem sarkoknál, rögzítés, páratartalom. Így ér célba épségben az értékes darab.',
    tag: 'Antikvitás',
  },
  {
    isoDate: '2025-01-08',
    date: '2025. január 8.',
    title: 'Járműpark: új, bővített raktérrel érkezett jármű',
    excerpt:
      'Nagyobb tételek és XXL csomagok szállítását is gördülékenyebben vállaljuk. Ha nem biztos a méretben, küldj fotót vagy méreteket – segítünk megtervezni a fuvar.',
    tag: 'Cég hír',
  },
];

function FaqPreviewIcon({ name }) {
  const svg = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  switch (name) {
    case 'clock':
      return (
        <svg {...svg}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case 'globe':
      return (
        <svg {...svg}>
          <circle cx="12" cy="12" r="9" />
          <path d="M2.5 12h19M12 2.5c2.5 4 2.5 15 0 19M12 2.5c-2.5 4-2.5 15 0 19" />
        </svg>
      );
    case 'box':
      return (
        <svg {...svg}>
          <path d="M12 2L3 7v10l9 5 9-5V7l-9-5zM3 7l9 5 9-5M12 12v10" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...svg}>
          <path d="M12 3l8 4v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V7l8-4z" />
        </svg>
      );
    case 'phone':
      return (
        <svg {...svg}>
          <path d="M5 4h4l2 5-2 1a12 12 0 006 6l1-2 5 2v4a1 1 0 01-1 1C9 17 4 10 3 5a1 1 0 011-1z" />
        </svg>
      );
    default:
      return (
        <svg {...svg}>
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
  }
}

export default function LandingPage() {
  const { t } = useI18n();
  const { pricingSettings } = usePricingSettings();
  const [quickFromFormatted, setQuickFromFormatted] = React.useState('');
  const [quickFromPlaceId, setQuickFromPlaceId] = React.useState('');
  const [quickToFormatted, setQuickToFormatted] = React.useState('');
  const [quickToPlaceId, setQuickToPlaceId] = React.useState('');
  const [quickLoading, setQuickLoading] = React.useState(false);
  const [quickResult, setQuickResult] = React.useState(null);
  const [quickError, setQuickError] = React.useState('');
  const [reviewPage, setReviewPage] = React.useState(0);
  const [contactName, setContactName] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [contactMessage, setContactMessage] = React.useState('');
  const [contactPrivacy, setContactPrivacy] = React.useState(false);
  const [contactError, setContactError] = React.useState('');
  const [contactSuccess, setContactSuccess] = React.useState(false);
  const [contactSubmitting, setContactSubmitting] = React.useState(false);
  const [gyikOpenIndex, setGyikOpenIndex] = React.useState(2);
  const refRail = React.useRef(null);
  const refDrag = React.useRef({ active: false, startX: 0, startScrollLeft: 0 });
  const [isMobileRail, setIsMobileRail] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
  });

  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = () => setIsMobileRail(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    onChange();
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  React.useEffect(() => {
    const scrollTo = location.state && location.state.scrollTo;
    if (!scrollTo) return;
    const el = document.getElementById(scrollTo);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      navigate('.', { replace: true, state: null });
    }
  }, [location.state, navigate]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactError('');
    setContactSuccess(false);
    if (!contactName.trim()) {
      setContactError(t('contact_err_name'));
      return;
    }
    if (!contactEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      setContactError(t('contact_err_email'));
      return;
    }
    if (!contactPhone.trim()) {
      setContactError(t('contact_err_phone'));
      return;
    }
    if (!contactMessage.trim()) {
      setContactError(t('contact_err_message'));
      return;
    }
    if (!contactPrivacy) {
      setContactError(t('gdpr_consent_required'));
      return;
    }

    const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8003').replace(/\/$/, '');
    setContactSubmitting(true);
    try {
      const response = await fetch(`${apiBase}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contactName.trim(),
          email: contactEmail.trim(),
          phone: contactPhone.trim(),
          message: contactMessage.trim(),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setContactError(data?.error || 'Nem sikerült elküldeni az üzenetet. Próbáld újra később.');
        return;
      }
      setContactSuccess(true);
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      setContactMessage('');
      setContactPrivacy(false);
    } catch {
      setContactError('Hálózati hiba. Ellenőrizd az internetkapcsolatot, vagy írj e-mailben.');
    } finally {
      setContactSubmitting(false);
    }
  };

  const REVIEWS_PER_PAGE = 3;
  const totalReviewPages = Math.ceil(REVIEWS.length / REVIEWS_PER_PAGE);
  const visibleReviews = REVIEWS.slice(
    reviewPage * REVIEWS_PER_PAGE,
    reviewPage * REVIEWS_PER_PAGE + REVIEWS_PER_PAGE,
  );

  React.useEffect(() => {
    const rail = refRail.current;
    if (!rail) return undefined;

    if (!isMobileRail) return undefined;

    let raf = 0;
    let last = performance.now();
    // pixels / second
    const speed = 70;

    const tick = (t) => {
      const dt = Math.min(60, t - last);
      last = t;

      if (!refDrag.current.active) {
        const half = rail.scrollWidth / 2;
        if (half > 0) {
          rail.scrollLeft += (speed * dt) / 1000;
          if (rail.scrollLeft >= half) rail.scrollLeft = rail.scrollLeft - half;
        }
      }

      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [isMobileRail]);

  const onRefPointerDown = (e) => {
    const rail = refRail.current;
    if (!rail) return;
    // only left click / primary touch
    if (e.button != null && e.button !== 0) return;
    refDrag.current.active = true;
    refDrag.current.startX = e.clientX;
    refDrag.current.startScrollLeft = rail.scrollLeft;
    rail.setPointerCapture?.(e.pointerId);
  };

  const onRefPointerMove = (e) => {
    const rail = refRail.current;
    if (!rail || !refDrag.current.active) return;
    const dx = e.clientX - refDrag.current.startX;
    rail.scrollLeft = refDrag.current.startScrollLeft - dx;
  };

  const onRefPointerUp = (e) => {
    const rail = refRail.current;
    if (!rail) return;
    refDrag.current.active = false;
    rail.releasePointerCapture?.(e.pointerId);
  };

  const quickCalcGenRef = React.useRef(0);

  const executeQuickCalc = React.useCallback(async () => {
    const from = quickFromFormatted.trim();
    const to = quickToFormatted.trim();
    if (!from || !to) {
      setQuickError('Válassz ki mindkét címet a Google keresőből.');
      return;
    }
    const gen = ++quickCalcGenRef.current;
    setQuickLoading(true);
    setQuickResult(null);
    setQuickError('');
    try {
      const km = await fetchLandingDrivingKm(quickFromPlaceId, quickToPlaceId, from, to);
      if (gen !== quickCalcGenRef.current) return;
      const { estimateNet: net } = computeQuickCalculatorEstimate(km, pricingSettings.quickCalculator);
      setQuickResult({
        km: String(km),
        price: formatLandingQuickHuf(net),
      });
    } catch (err) {
      if (gen !== quickCalcGenRef.current) return;
      setQuickError(err?.message || 'Nem sikerült kiszámolni. Próbáld pontosabb címekkel.');
    } finally {
      if (gen === quickCalcGenRef.current) setQuickLoading(false);
    }
  }, [quickFromFormatted, quickFromPlaceId, quickToFormatted, quickToPlaceId, pricingSettings.quickCalculator]);

  React.useEffect(() => {
    const from = quickFromFormatted.trim();
    const to = quickToFormatted.trim();
    if (from && to) void executeQuickCalc();
    return () => {
      quickCalcGenRef.current += 1;
    };
  }, [quickFromFormatted, quickFromPlaceId, quickToFormatted, quickToPlaceId, executeQuickCalc]);

  return (
    <>
      <header className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <p className="hero-kicker">
              Belföldi és nemzetközi különleges szállítás – Antikvitás, műtárgyak,
              <br />
              orvosi eszközök
            </p>
            <h1>Biztonságos áruszállítás, egyedi igényekre szabva</h1>
            <p>
              Megbízható, szakmailag magas színvonalú és egyre komplexebb szolgáltatásokat nyújtunk
              ügyfeleink számára.
            </p>
            <div className="hero-actions">
              {isExternalCampaignHref(AJANLAT_PAGE_URL) ? (
                <a className="primary-btn" href={AJANLAT_PAGE_URL} rel="noopener noreferrer">
                  Ajánlatot kérek
                </a>
              ) : (
                <Link className="primary-btn" to={AJANLAT_PAGE_URL}>
                  Ajánlatot kérek
                </Link>
              )}
              <a className="secondary-btn" href="#szolgaltatas">
                Szolgáltatások
              </a>
              <Link className="secondary-btn" to="/rolunk">
                Rólunk
              </Link>
            </div>
          </div>

          <div className="hero-right">
            <div className="hero-right-inner">
              <div className="quick-card" id="gyorsarazas">
                <div className="quick-layout">
                  <div className="quick-route-rail" aria-hidden="true">
                    <div className="quick-route-point">A</div>
                    <svg
                      className="quick-route-svg"
                      viewBox="0 0 96 220"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        className="quick-route-track"
                        pathLength="100"
                        d="M48 10
         C48 34 48 42 58 53
         C75 72 75 95 54 112
         C35 128 34 150 48 171
         C48 180 48 188 48 198"
                      />
                      <path
                        className="quick-route-line"
                        pathLength="100"
                        d="M48 10
         C48 34 48 42 58 53
         C75 72 75 95 54 112
         C35 128 34 150 48 171
         C48 180 48 188 48 198"
                      />
                      <path
                        className="quick-route-flow"
                        pathLength="100"
                        d="M48 10
         C48 34 48 42 58 53
         C75 72 75 95 54 112
         C35 128 34 150 48 171
         C48 180 48 188 48 198"
                      />
                      <circle className="quick-route-dot" cx="48" cy="10" r="5" />
                      <circle className="quick-route-dot" cx="48" cy="198" r="5" />
                    </svg>
                    <div className="quick-route-point">B</div>
                  </div>

                <div className="quick-main">
                  <div className="modal-header quick-header">
                    <div className="modal-title-group quick-title-block">
                      <p className="modal-kicker">KALKULÁLJON GYORSAN</p>
                      <h3>Gyors árazás</h3>
                    </div>
                    <div className="modal-logo">
                      <img src={logo} alt="Cég logó" />
                    </div>
                  </div>
                  <p className="quick-disclaimer">
                    Az alábbi Kalkulátor csak közelítő nettó árat mutat, nem veszi figyelembe, csak a kilométert!
                  </p>
                  {quickError ? (
                    <p className="quick-calc-error" role="alert">
                      {quickError}
                    </p>
                  ) : null}

                  <div className="quick-row">
                    <GoogleAddressAutocomplete
                      id="landing-quick-from"
                      label="Honnan?"
                      value={quickFromFormatted}
                      fieldClassName="field compact quick-gmaps-field"
                      pillRounded
                      onResolved={(addr) => {
                        setQuickFromFormatted(addr.formatted || '');
                        setQuickFromPlaceId(addr.placeId || '');
                        setQuickResult(null);
                        setQuickError('');
                      }}
                    />
                  </div>

                  <div className="quick-row">
                    <GoogleAddressAutocomplete
                      id="landing-quick-to"
                      label="Hova?"
                      value={quickToFormatted}
                      fieldClassName="field compact quick-gmaps-field"
                      pillRounded
                      onResolved={(addr) => {
                        setQuickToFormatted(addr.formatted || '');
                        setQuickToPlaceId(addr.placeId || '');
                        setQuickResult(null);
                        setQuickError('');
                      }}
                    />
                  </div>

                  <div className="quick-footer">
                    <div className="quick-meta">
                      {quickLoading ? (
                        <div className="skeleton-group">
                          <div className="skeleton-line short" />
                          <div className="skeleton-line" />
                        </div>
                      ) : (
                        <div className="quick-meta-columns">
                          <div className="quick-meta-item">
                            <span className="quick-meta-label">km</span>
                            <span className="quick-meta-value">
                              {quickResult ? `${quickResult.km} km` : '—'}
                            </span>
                          </div>
                          <div className="quick-meta-item">
                            <span className="quick-meta-label">ár</span>
                            <span className="quick-meta-value">
                              {quickResult ? quickResult.price : '—'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <button type="button" className="quick-submit" onClick={() => void executeQuickCalc()}>
                      {quickLoading ? 'Számolás...' : 'Kalkulálás'}
                    </button>
                  </div>
                </div>
              </div>
              </div>
              <p className="hero-quick-footnote">
                Pontosabb árajánlat kéréséhez használja „Ajánlatot kérek / Szolgáltatások” gombot.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section id="szolgaltatas" className="section services">
          <h2>Szolgáltatásaink</h2>
          <p className="services-sub">
            Megbízható megoldások belföldi, nemzetközi és speciális szállítási igényekre.
          </p>
          <div className="service-grid">
            <article className="service-column service-column--card">
              <h3 className="service-column-title">
                <span className="service-column-icon" aria-hidden="true">
                  🚚
                </span>
                Országos és nemzetközi szállítás
              </h3>
              <ul className="service-list service-list--card">
                <li>
                  <Link to="/szolgaltatas/csomagszallitas">Csomagszállítás</Link>
                </li>
                <li>
                  <Link to="/szolgaltatas/koltoztetes">Költöztetés</Link>
                </li>
                <li>
                  <Link to="/szolgaltatas/raklapos-szallitas">Raklapos szállítás</Link>
                </li>
                <li>
                  <Link to="/szolgaltatas/butor-szallitas">Bútorszállítás</Link>
                </li>
                <li>
                  <Link to="/szolgaltatas/kerekpar-szallitas">Kerékpár–motorkerékpár szállítás</Link>
                </li>
                <li>
                  <Link to="/szolgaltatas/ikea-hazhozszallitas">IKEA szállítás</Link>
                </li>
                <li>
                  <Link to="/ajanlat/csomagszallitas">Különleges bánásmódot igénylő szállítás</Link>
                </li>
              </ul>
            </article>

            <article className="service-column service-column--card" id="szolgaltatas-kulonleges">
              <h3 className="service-column-title">
                <span className="service-column-icon" aria-hidden="true">
                  📦
                </span>
                Speciális szállítás
              </h3>
              <ul className="service-list service-list--card">
                <li>
                  <Link to="/szolgaltatas/butor-szallitas">Antikvitás</Link>
                </li>
                <li>
                  <Link to="/szolgaltatas/csomagszallitas">Orvosi eszközök</Link>
                </li>
                <li>
                  <Link to="/szolgaltatas/csomagszallitas">Trófea szállítás</Link>
                </li>
              </ul>
            </article>
          </div>
        </section>

        {SHOW_AKTUALIS_PROMOS ? (
          <section id="akciok" className="section promotions">
            <h2>Aktuális / Akciók</h2>
            <p>
              Időszakos kedvezmények, speciális ajánlatok és egyedi konstrukciók cégeknek és
              magánszemélyeknek. Kérj ajánlatot, és segítünk a legjobb megoldást megtalálni.
            </p>
            {(() => {
              const routeCampaign = CAMPAIGNS.find((c) => c.id === 'route-discount' && c.enabled);

              return (
                <div className="announcements">
                  {routeCampaign && Array.isArray(routeCampaign.routes) && routeCampaign.routes.length > 0 && (
                    <div className="announcement-board">
                      <div className="announcement-head">
                        <div>
                          <div className="announcement-title">{routeCampaign.title}</div>
                          <div className="announcement-sub">{routeCampaign.body}</div>
                        </div>
                        {isExternalCampaignHref(routeCampaign.ctaTo) ? (
                          <a
                            className="nav-pill nav-pill-cta announcement-cta"
                            href={routeCampaign.ctaTo}
                            rel="noopener noreferrer"
                          >
                            {routeCampaign.ctaLabel}
                          </a>
                        ) : (
                          <Link
                            className="nav-pill nav-pill-cta announcement-cta"
                            to={routeCampaign.ctaTo}
                            state={routeCampaign.ctaState}
                          >
                            {routeCampaign.ctaLabel}
                          </Link>
                        )}
                      </div>

                      <div className="announcement-list" role="list">
                        {routeCampaign.routes.map((r, idx) => (
                          <div className="announcement-item" role="listitem" key={`route-${idx}`}>
                            <div className="announcement-when">{r.when}</div>
                            <div className="announcement-route">
                              <strong>
                                {r.from} → {r.to}
                              </strong>
                              <span className="announcement-note">Útvonal mentén felvétel/lerakás lehetséges</span>
                            </div>
                            <div className="announcement-badge">-{r.discountPct}%</div>
                            {isExternalCampaignHref(routeCampaign.ctaTo) ? (
                              <a
                                className="nav-pill announcement-row-cta"
                                href={routeCampaign.ctaTo}
                                rel="noopener noreferrer"
                              >
                                Érdekel
                              </a>
                            ) : (
                              <Link
                                className="nav-pill announcement-row-cta"
                                to={routeCampaign.ctaTo}
                                state={routeCampaign.ctaState}
                              >
                                Érdekel
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {null}
                </div>
              );
            })()}
          </section>
        ) : null}

        <section id="gyik" className="section faq-preview" aria-labelledby="faq-preview-title">
          <div className="faq-preview-inner">
            <div className="faq-preview-hero">
              <div className="faq-preview-hero-text">
                <span className="faq-preview-badge">GYIK</span>
                <h2 id="faq-preview-title" className="faq-preview-title">
                  Gyakran ismételt kérdések
                </h2>
                <p className="faq-preview-lead">
                  Válaszokat adunk a leggyakoribb kérdésekre, hogy gyorsan és egyszerűen megtaláld, amire
                  szükséged van.
                </p>
              </div>
              <div className="faq-preview-hero-visual">
                <div className="faq-preview-visual-bg" aria-hidden="true" />
                <img
                  className="faq-preview-illustration"
                  src={faqPreviewIllustration}
                  alt=""
                  width={1448}
                  height={1086}
                  decoding="async"
                />
              </div>
            </div>

            <div className="faq-preview-accordions" role="list">
              {FAQS.slice(0, 5).map((f, idx) => {
                const open = gyikOpenIndex === idx;
                const icon = f.icon || 'clock';
                return (
                  <div
                    key={f.q}
                    className={`faq-preview-acc${open ? ' faq-preview-acc--open' : ''}`}
                    role="listitem"
                  >
                    <button
                      type="button"
                      className="faq-preview-acc-trigger"
                      aria-expanded={open}
                      id={`faq-preview-trigger-${idx}`}
                      aria-controls={`faq-preview-panel-${idx}`}
                      onClick={() => setGyikOpenIndex((prev) => (prev === idx ? -1 : idx))}
                    >
                      <span className="faq-preview-acc-icon" aria-hidden="true">
                        <FaqPreviewIcon name={icon} />
                      </span>
                      <span className="faq-preview-acc-q">{f.q}</span>
                      <span className="faq-preview-acc-chevron" aria-hidden="true" />
                    </button>
                    <div
                      id={`faq-preview-panel-${idx}`}
                      role="region"
                      aria-labelledby={`faq-preview-trigger-${idx}`}
                      aria-hidden={!open}
                      className={`faq-preview-acc-panel-outer${open ? ' faq-preview-acc-panel-outer--open' : ''}`}
                    >
                      <div className="faq-preview-acc-panel-inner">
                        <div className="faq-preview-acc-panel">
                          <p className="faq-preview-acc-a">{f.a}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Link className="faq-preview-see-all" to="/gyik">
              <span>Összes kérdés megtekintése</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M5 12h14M13 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>
        </section>

        <section id="referenciak" className="section references">
          <h2>{t('landing_partners')}</h2>
          <div
            className="ref-grid"
            ref={refRail}
            onPointerDown={onRefPointerDown}
            onPointerMove={onRefPointerMove}
            onPointerUp={onRefPointerUp}
            onPointerCancel={onRefPointerUp}
            onPointerLeave={onRefPointerUp}
          >
            {(isMobileRail ? [...PARTNER_LOGOS, ...PARTNER_LOGOS] : PARTNER_LOGOS).map((p, idx) => (
              <div className="ref-item" key={`${idx}-${p.src}`}>
                <img src={p.src} alt={p.alt} />
              </div>
            ))}
          </div>

          <div className="reviews">
            <h3>{t('landing_reviews')}</h3>
            <div className="review-list">
              {visibleReviews.map((r) => (
                <article className="review-card" key={r.name}>
                  <div className="review-header">
                    <div className="review-avatar" style={{ background: r.avatarColor || '#1a73e8' }}>
                      {r.initial}
                    </div>
                    <div>
                      <div className="review-name">{r.name}</div>
                      <ReviewMeta meta={r.meta} />
                    </div>
                  </div>
                  <StarRow rating={r.rating} />
                  <p className="review-body">{r.text}</p>
                </article>
              ))}
            </div>
            <div className="review-controls">
              <button
                type="button"
                className="review-nav"
                onClick={() => setReviewPage((p) => (p > 0 ? p - 1 : p))}
                disabled={reviewPage === 0}
              >
                ‹
              </button>
              <span className="review-page-indicator">
                {reviewPage + 1} / {totalReviewPages}
              </span>
              <button
                type="button"
                className="review-nav"
                onClick={() => setReviewPage((p) => (p < totalReviewPages - 1 ? p + 1 : p))}
                disabled={reviewPage === totalReviewPages - 1}
              >
                ›
              </button>
            </div>
          </div>
        </section>

        <section id="hirek" className="section news">
          <h2>Hírek</h2>
          <p className="news-intro">
            Legfrissebb híreink, tippek és változások a szállításról – válogatás a mindennapi
            fuvarokhoz.
          </p>
          <div className="news-grid">
            {NEWS_POSTS.map((post) => (
              <article className="news-card" key={post.title}>
                <div className="news-card-meta">
                  <time dateTime={post.isoDate}>{post.date}</time>
                  <span className="news-tag">{post.tag}</span>
                </div>
                <h3 className="news-card-title">{post.title}</h3>
                <p className="news-card-excerpt">{post.excerpt}</p>
                <span className="news-card-fake-link">Bővebben (hamarosan)</span>
              </article>
            ))}
          </div>
        </section>

        <section id="kapcsolat" className="section contact">
          <h2>Kapcsolat</h2>
          <div className="contact-grid">
            <div className="contact-info">
              <h3>ProfiTrans‑Mitter Kft.</h3>

              <div className="contact-info-block">
                <div className="contact-info-block-title">Cégadatok</div>
                <p>H‑2119 Pécel, Határ u. 3. (5. épület, 2. emelet)</p>
                <p>GPS: 47.4910, 19.3178</p>
                <p>
                  <a href="tel:+36706050279">Tel: +36 70 605 0279</a>
                </p>
                <p>
                  <a href="mailto:office@moveandparcel.eu">E‑mail: office@moveandparcel.eu</a>
                </p>
                <p>Adószám: 22913656-2-13, HU22913656</p>
              </div>

              <div className="contact-info-block">
                <div className="contact-info-block-title">Kapcsolattartók</div>
                <h4>Kiss Zsolt ügyvezető</h4>
                <p>
                  <a href="tel:+36703615497">Mobil: +36 70 361 5497</a>
                </p>

                <h4>Kiss Noel Ádám üzletágvezető</h4>
                <p>
                  <a href="tel:+36309461794">Mobil: +36 30 946 1794</a>
                </p>
              </div>

              <div className="contact-info-block">
                <div className="contact-info-block-title">XXL csomagok</div>
                <p>
                  <a href="tel:+36706050279">XXL méretű csomag szállítása: +36 70 605 0279</a>
                </p>
                <p>
                  <a href="tel:+36709409507">XXL csomag nyomon követés: +36 70 940 9507</a>
                </p>
              </div>
            </div>

            <div className="contact-form-wrapper">
              <h3>Írj nekünk</h3>
              {contactError && (
                <div className="contact-form-alert contact-form-alert--error" role="alert">
                  {contactError}
                </div>
              )}
              {contactSuccess && (
                <div className="contact-form-alert contact-form-alert--success" role="status">
                  {t('contact_success')}
                </div>
              )}
              <form className="contact-form" onSubmit={handleContactSubmit} noValidate>
                <div className="field">
                  <label htmlFor="contact-name">Név</label>
                  <input
                    id="contact-name"
                    type="text"
                    placeholder="Teljes név"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    autoComplete="name"
                  />
                </div>
                <div className="field">
                  <label htmlFor="contact-email">E‑mail</label>
                  <input
                    id="contact-email"
                    type="email"
                    placeholder="email@cimed.hu"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
                <div className="field">
                  <label htmlFor="contact-phone">Telefon</label>
                  <input
                    id="contact-phone"
                    type="tel"
                    placeholder="+36 30 123 4567"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    autoComplete="tel"
                  />
                </div>
                <div className="field">
                  <label htmlFor="contact-message">Üzenet</label>
                  <textarea
                    id="contact-message"
                    rows="4"
                    placeholder="Röviden írd le, miben segíthetünk."
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                  />
                </div>
                <GdprConsentCheckbox
                  id="landing-contact-gdpr"
                  checked={contactPrivacy}
                  onChange={setContactPrivacy}
                />
                <button type="submit" className="primary-btn" disabled={contactSubmitting}>
                  {contactSubmitting ? 'Küldés…' : 'Üzenet küldése'}
                </button>
              </form>
            </div>

            <div className="contact-map-wrapper">
              <h3>Térképen</h3>
              <div className="contact-map-embed">
                <iframe
                  title="2119 Pécel, Határ u. 3."
                  src="https://maps.google.com/maps?q=2119%20P%C3%A9cel%2C%20Hat%C3%A1r%20u.%203.&t=m&z=10&output=embed&iwloc=near"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="contact-social-card">
                <div className="contact-social-title">Kövess minket</div>
                <div className="contact-social-links" aria-label="Move and Parcel közösségi oldalak">
                  {SOCIAL_LINKS.map((item) => (
                    <a
                      key={item.label}
                      className="contact-social-link"
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={item.label}
                    >
                      {item.icon ? (
                        <img className="contact-social-icon" src={item.icon} alt="" aria-hidden="true" />
                      ) : (
                        <span className="contact-social-fallback" aria-hidden="true">
                          {item.fallback}
                        </span>
                      )}
                      <span>{item.label}</span>
                    </a>
                  ))}
                </div>
                <a
                  className="contact-review-btn"
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  Google értékelés írása
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
